import { useEffect, useState, useCallback, useMemo, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain, Percent, ShoppingBasket, Users, ChefHat,
  ArrowRight, ChevronLeft, ChevronRight, Settings2, Check,
} from "lucide-react";
import { Card } from "../ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import api from "../../lib/api";
import { useDashboardPreferences } from "../../hooks/useDashboardPreferences";
import { useDashboardAlerts } from "../../hooks/useDashboardAlerts";

// ── Card-id catalog ────────────────────────────────────────────────────
// Used by the personalization popover so users can hide individual cards.
// IDs are stable & persisted in dashboard preferences (per user × restaurant).
//
// Two flavours:
//   • alert-* — sourced from `useDashboardAlerts` (severity-driven, critical
//     items appear first in red/amber). Replaces the old standalone
//     DashboardAlertsStack banners — single attention zone for the user.
//   • Operative signals (no "alert-" prefix) — Rail-exclusive cards.
const KNOWN_CARD_IDS = [
  // Alerts (from useDashboardAlerts; ordered by severity at render time)
  { id: "alert-low_stock",        label: "Stock bajo mínimos" },
  { id: "alert-merma_oculta",     label: "Merma oculta" },
  { id: "alert-archived_soon",    label: "Restaurante por borrarse" },
  { id: "alert-low_credits",      label: "Créditos IA bajos" },
  { id: "alert-invoices_unpaid",  label: "Facturas sin pagar" },
  { id: "alert-invoices_pending", label: "Documentos en bandeja" },
  { id: "alert-finance_insight",  label: "Insight contable IA" },
  { id: "alert-savings",          label: "Oportunidad de ahorro" },
  // Operative signals (Rail-only)
  { id: "tpv_active",       label: "Mesas activas (TPV)" },
  { id: "shopping_ready",   label: "Lista de compras lista" },
  { id: "production_reco",  label: "Recomendaciones de producción" },
  { id: "food_cost",        label: "Food-cost" },
  { id: "weekly_brief",     label: "Brief semanal IA" },
  { id: "loyalty",          label: "Fidelización" },
];

// Map an alert.id (from useDashboardAlerts) to a stable canonical kind so
// hide-prefs survive when the underlying count changes (e.g., "low-stock-83"
// → "low_stock"; tomorrow it might be "low-stock-79" but it's the same kind).
function alertKind(alert) {
  const id = alert?.id || "";
  if (id.startsWith("low-stock-"))      return "low_stock";
  if (id.startsWith("variance-"))       return "merma_oculta";
  if (id.startsWith("archived-soon-"))  return "archived_soon";
  if (id.startsWith("savings-"))        return "savings";
  if (id.startsWith("low-credits-"))    return "low_credits";
  if (id.startsWith("finance-unpaid-")) return "invoices_unpaid";
  if (id.startsWith("finance-pending-"))return "invoices_pending";
  if (id.startsWith("finance-insight-"))return "finance_insight";
  return id;
}
const SEVERITY_TONE = { critical: "rose", warning: "amber", info: "emerald" };

/**
 * Insight Rail — Horizontal scrollable strip of "insight cards" that
 * replaces the old vertical stack of 5+ banners on the Dashboard.
 *
 * Why horizontal?
 *   • Mobile/tablet: swipe is the natural gesture
 *   • Desktop: scroll-snap + arrow buttons keep parity
 *   • Saves ~600 px of vertical space (4-5 banners → 1 row)
 *
 * Cards are dynamic — fetched from existing endpoints. Each card has its
 * own data-test-id and navigates to the relevant module on click. A card
 * only renders if its underlying signal is non-empty (no "0 productos
 * bajo" card cluttering the rail). The user can additionally hide any
 * card type via the gear popover; preferences persist per-restaurant.
 *
 * Anti-flicker strategy (Feb 2026, iter.204):
 *   • Skeleton shows ONLY on first load (when `insights.length === 0`).
 *   • On every subsequent refresh we keep the existing cards visible
 *     (stale-while-revalidate). The transition is invisible to the user.
 */
export default function InsightRail({ restaurantId }) {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);

  // ── Hidden-card preferences (per user × restaurant, synced w/ backend) ─
  const { preferences, setPreference } = useDashboardPreferences(restaurantId);
  const hiddenIds = useMemo(
    () => new Set(preferences.insight_rail_hidden || []),
    [preferences.insight_rail_hidden],
  );

  const toggleHidden = (id) => {
    // Read the latest list straight from `preferences` (not from the
    // derived `hiddenIds` Set) to avoid stale-closure races when the user
    // toggles multiple cards quickly. `setPreference` itself does the
    // optimistic update + backend PATCH atomically per call.
    const current = new Set(preferences.insight_rail_hidden || []);
    if (current.has(id)) current.delete(id); else current.add(id);
    setPreference("insight_rail_hidden", [...current]);
  };
  const resetHidden = () => {
    setPreference("insight_rail_hidden", []);
  };

  // ── Aggregate fetch — pulls all the signals in parallel ───────────────
  const refresh = useCallback(async () => {
    if (!restaurantId) return;
    // Only show skeleton on the very first load. On refetches we keep the
    // previous cards visible to avoid the "skeleton flash" flicker.
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.request(`/restaurants/${restaurantId}/dashboard`),
        api.request(`/restaurants/${restaurantId}/tpv/orders/active`).catch(() => ({ orders: [] })),
        api.request(`/restaurants/${restaurantId}/shopping-list/pending-summary`).catch(() => null),
        api.request(`/restaurants/${restaurantId}/production/recommendations`).catch(() => null),
      ]);
      const [dashboardR, tpvR, shoppingR, productionR] = results;

      const dashboard = dashboardR.status === "fulfilled" ? dashboardR.value : {};
      const tpv = tpvR.status === "fulfilled" ? tpvR.value : { orders: [] };
      const shopping = shoppingR.status === "fulfilled" ? shoppingR.value : null;
      const production = productionR.status === "fulfilled" ? productionR.value : null;

      const cards = [];

    // 1. Active TPV tables (real-time operational signal)
    const activeTables = (tpv?.orders || []).filter(o => o.status === "active").length;
    if (activeTables > 0) {
      cards.push({
        id: "tpv_active",
        icon: ShoppingBasket,
        tone: "primary",
        title: `${activeTables} mesa${activeTables === 1 ? "" : "s"} activa${activeTables === 1 ? "" : "s"}`,
        body: "Servicio en marcha · pulsa para gestionar",
        cta: "Ir al TPV",
        onClick: () => navigate("/app/tpv"),
      });
    }

    // 2. (REMOVED — "low_stock" is now sourced from useDashboardAlerts so
    //     the count is authoritative and we don't show two cards with
    //     different numbers for the same metric.)

    // 3. Shopping list ready
    if (shopping?.pending_items > 0) {
      cards.push({
        id: "shopping_ready",
        icon: ShoppingBasket,
        tone: "blue",
        title: `Lista de compras: ${shopping.pending_items} ítems`,
        body: shopping.estimated_total
          ? `Estimado ${formatMoney(shopping.estimated_total)} — lista para enviar`
          : "Genera el pedido en 1 clic",
        cta: "Ver lista",
        onClick: () => navigate("/app/shopping"),
      });
    }

    // 4. Production recommendations (only if any)
    if ((production?.recommendations || []).length > 0) {
      const top = production.recommendations[0];
      cards.push({
        id: "production_reco",
        icon: ChefHat,
        tone: "emerald",
        title: `Producción: prepara ${production.recommendations.length} elaboración${production.recommendations.length === 1 ? "" : "es"}`,
        body: top ? `Principal: ${top.batch_recipe_name || top.name}` : "Basado en ventas previstas",
        cta: "Ver producción",
        onClick: () => navigate("/app/production"),
      });
    }

    // 5. Food-cost (if data available — uses the FoodCostKPI panel under the hood)
    if (dashboard?.food_cost?.current_pct !== undefined && dashboard?.food_cost?.current_pct !== null) {
      const pct = dashboard.food_cost.current_pct;
      const classification = dashboard.food_cost.classification || "good";
      const toneMap = { excellent: "emerald", good: "emerald", warning: "amber", critical: "rose" };
      cards.push({
        id: "food_cost",
        icon: Percent,
        tone: toneMap[classification] || "emerald",
        title: `Food-cost: ${pct.toFixed(1)}%`,
        body: `Media 30d ${dashboard.food_cost.avg_pct?.toFixed(1) || "—"}% · ${classification}`,
        cta: "Detalle",
        onClick: () => navigate("/app/finance?tab=forecast"),
      });
    }

    // 6. (REMOVED — "merma_oculta" is now sourced from useDashboardAlerts.)

    // 7. AI weekly brief teaser — always last operative card (entry-point to the existing WeeklyBrief panel)
    cards.push({
      id: "weekly_brief",
      icon: Brain,
      tone: "violet",
      title: "Tu semana en 60 segundos",
      body: "Resumen ejecutivo generado por IA",
      cta: "Abrir resumen",
      onClick: () => {
        // The WeeklyBrief panel lives in the Bento section below. We try to
        // find it and expand it; if it isn't mounted (e.g. RBAC denied the
        // brief endpoint, or the panel returned null for any reason), we
        // gracefully fall back to navigating to /app/finance where the
        // same data is available — so the card never feels broken.
        const tryExpand = () => {
          const toggle = document.querySelector("[data-testid='weekly-brief-toggle']");
          if (!toggle) return false;
          const body = document.querySelector("[data-testid='weekly-brief-body']");
          if (!body) toggle.click();
          toggle.scrollIntoView({ behavior: "smooth", block: "center" });
          return true;
        };
        if (tryExpand()) return;
        // Retry a few times in case the bento mounted right after click
        let attempts = 0;
        const interval = setInterval(() => {
          attempts += 1;
          if (tryExpand() || attempts >= 6) {
            clearInterval(interval);
            if (attempts >= 6) navigate("/app/finance");
          }
        }, 200);
      },
    });

    // 8. Loyalty teaser (only when there are loyal customers)
    if ((dashboard?.loyalty?.total_customers ?? 0) > 0) {
      cards.push({
        id: "loyalty",
        icon: Users,
        tone: "pink",
        title: `${dashboard.loyalty.total_customers} clientes fidelizados`,
        body: `${dashboard.loyalty.total_points_issued || 0} puntos emitidos`,
        cta: "Ver clientes",
        onClick: () => navigate("/app/customers"),
      });
    }

    // 9. (REMOVED — "invoices_pending" is now sourced from useDashboardAlerts
    //     so the user only sees one signal per topic, ordered by severity.)

      setInsights(cards);
    } catch (e) {
      console.error("[InsightRail] refresh error", e);
      // Only clear cards if this is the first load. On refetch failure we
      // keep stale cards so the user doesn't see a sudden empty rail.
      if (!hasLoadedOnce.current) setInsights([]);
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
    }
  }, [restaurantId, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Scroll controls (desktop arrows; mobile uses native swipe) ─────────
  const railId = "insight-rail-scroll";
  const scrollBy = (delta) => {
    const el = document.getElementById(railId);
    if (el) el.scrollBy({ left: delta, behavior: "smooth" });
  };

  // ── Alerts (source of truth for severity-driven signals) ──────────────
  // Pulled from the same hook the old DashboardAlertsStack used. They
  // now render INSIDE the rail (sorted critical → warning → info), so the
  // user has a single attention zone instead of competing strips/banners.
  const { alerts } = useDashboardAlerts(restaurantId);
  const alertCards = useMemo(() => alerts.map(a => {
    const kind = alertKind(a);
    const action = a.actions?.[0];
    return {
      id: `alert-${kind}`,
      icon: a.icon,
      tone: SEVERITY_TONE[a.severity] || "amber",
      severity: a.severity,
      title: a.title,
      body: a.description,
      cta: action?.label || "Ver",
      onClick: () => {
        if (!action) return;
        if (typeof action.onClick === "function") return action.onClick();
        if (action.href) navigate(action.href);
      },
    };
  }), [alerts, navigate]);

  // Apply user's hide-card preferences. Alerts come first (already sorted
  // by severity inside useDashboardAlerts), then the operative signals.
  const visibleInsights = useMemo(
    () => [...alertCards, ...insights].filter(c => !hiddenIds.has(c.id)),
    [alertCards, insights, hiddenIds],
  );

  // First-load skeleton (only when we have never had any insights yet).
  // Subsequent refetches keep the previous cards visible to avoid flicker.
  if (loading && !hasLoadedOnce.current) {
    return (
      <section className="relative" data-testid="insight-rail-loading">
        <div className="flex items-baseline justify-between mb-2 px-1">
          <h2 className="font-heading text-sm font-semibold text-foreground/70 uppercase tracking-wider">
            Vistazo rápido
          </h2>
        </div>
        <div className="flex gap-3 overflow-hidden pb-2 pr-2 -mx-1 px-1">
          {[0, 1, 2, 3].map(i => (
            <div
              key={i}
              className="shrink-0 w-[280px] sm:w-[300px] h-[148px] rounded-xl bg-muted/40 animate-pulse"
              aria-hidden="true"
            />
          ))}
        </div>
      </section>
    );
  }

  // Hide the rail entirely when there is nothing to show (all cards either
  // empty signal or hidden by the user).
  if (visibleInsights.length === 0) return null;

  return (
    <section
      className="relative"
      data-testid="insight-rail"
    >
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h2 className="font-heading text-sm font-semibold text-foreground/70 uppercase tracking-wider">
          Vistazo rápido
        </h2>
        <div className="flex items-center gap-1">
          {/* Personalization popover: hide/show individual card types */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="h-8 w-8 rounded-full border border-border/60 bg-card text-muted-foreground hover:text-foreground hover:bg-muted/60 transition flex items-center justify-center"
                aria-label="Personalizar vistazo rápido"
                data-testid="insight-rail-customize"
              >
                <Settings2 className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3" data-testid="insight-rail-customize-popover">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Vistazo rápido
                </p>
                {hiddenIds.size > 0 && (
                  <button
                    type="button"
                    onClick={resetHidden}
                    className="text-[11px] text-primary hover:underline"
                    data-testid="insight-rail-customize-reset"
                  >
                    Restablecer
                  </button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mb-1 leading-snug">
                Activa las tarjetas que quieres ver. Tu elección se sincroniza
                en todos tus dispositivos.
              </p>
              <p className="text-[11px] text-muted-foreground/80 mb-3 leading-snug italic">
                Algunas tarjetas solo aparecen cuando hay datos relevantes
                (p. ej. stock bajo mínimos solo si los hay).
              </p>
              <div className="space-y-1 max-h-72 overflow-y-auto -mx-1 px-1">
                {KNOWN_CARD_IDS.map(({ id, label }, idx) => {
                  const active = !hiddenIds.has(id);
                  // Insert a divider between alerts and operative signals
                  // so the user understands the two flavours.
                  const isFirstOperative = id === "tpv_active";
                  return (
                    <Fragment key={id}>
                      {isFirstOperative && (
                        <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">
                          Operativos
                        </div>
                      )}
                      {idx === 0 && (
                        <div className="px-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">
                          Alertas
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleHidden(id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm transition-colors ${
                          active ? "bg-primary/5 text-foreground" : "text-muted-foreground hover:bg-muted/60"
                        }`}
                        data-testid={`insight-rail-toggle-${id}`}
                      >
                        <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                          active ? "bg-primary border-primary text-primary-foreground" : "border-border"
                        }`}>
                          {active && <Check className="h-3 w-3" />}
                        </span>
                        <span className="flex-1 truncate">{label}</span>
                      </button>
                    </Fragment>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          <div className="hidden sm:flex items-center gap-1">
            <button
              type="button" onClick={() => scrollBy(-320)}
              className="h-8 w-8 rounded-full border border-border/60 bg-card text-muted-foreground hover:text-foreground hover:bg-muted/60 transition flex items-center justify-center"
              aria-label="Scroll left" data-testid="insight-rail-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button" onClick={() => scrollBy(320)}
              className="h-8 w-8 rounded-full border border-border/60 bg-card text-muted-foreground hover:text-foreground hover:bg-muted/60 transition flex items-center justify-center"
              aria-label="Scroll right" data-testid="insight-rail-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        id={railId}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 pr-2 -mx-1 px-1
                   scrollbar-thin scrollbar-thumb-muted-foreground/20"
        style={{ scrollbarWidth: "thin" }}
        data-testid="insight-rail-scroll"
      >
        {visibleInsights.map(card => (
          <InsightCard key={card.id} card={card} />
        ))}
      </div>
    </section>
  );
}

const TONE_STYLES = {
  primary:  { bg: "bg-primary/10",       icon: "text-primary",       border: "border-primary/20" },
  amber:    { bg: "bg-amber-50 dark:bg-amber-950/30",       icon: "text-amber-700 dark:text-amber-400",       border: "border-amber-200 dark:border-amber-900/40" },
  blue:     { bg: "bg-blue-50 dark:bg-blue-950/30",         icon: "text-blue-600 dark:text-blue-400",         border: "border-blue-200 dark:border-blue-900/40" },
  emerald:  { bg: "bg-emerald-50 dark:bg-emerald-950/30",   icon: "text-emerald-700 dark:text-emerald-400",   border: "border-emerald-200 dark:border-emerald-900/40" },
  rose:     { bg: "bg-rose-50 dark:bg-rose-950/30",         icon: "text-rose-700 dark:text-rose-400",         border: "border-rose-200 dark:border-rose-900/40" },
  violet:   { bg: "bg-violet-50 dark:bg-violet-950/30",     icon: "text-violet-600 dark:text-violet-400",     border: "border-violet-200 dark:border-violet-900/40" },
  pink:     { bg: "bg-pink-50 dark:bg-pink-950/30",         icon: "text-pink-600 dark:text-pink-400",         border: "border-pink-200 dark:border-pink-900/40" },
};

function InsightCard({ card }) {
  const Icon = card.icon;
  const tone = TONE_STYLES[card.tone] || TONE_STYLES.primary;
  return (
    <Card
      onClick={card.onClick}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); card.onClick?.(); } }}
      data-testid={`insight-card-${card.id}`}
      className={`shrink-0 w-[280px] sm:w-[300px] p-4 snap-start cursor-pointer
                  transition-all hover:-translate-y-0.5 hover:shadow-md
                  border ${tone.border}
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
    >
      <div className={`inline-flex p-2 rounded-lg ${tone.bg} ${tone.icon} mb-2.5`}>
        <Icon className="w-4 h-4" />
      </div>
      <h3 className="font-heading text-sm font-bold leading-snug mb-1 line-clamp-2">{card.title}</h3>
      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{card.body}</p>
      <div className={`inline-flex items-center gap-1 text-xs font-medium ${tone.icon}`}>
        {card.cta}
        <ArrowRight className="w-3 h-3" />
      </div>
    </Card>
  );
}

function formatMoney(n) {
  if (typeof n !== "number") return "—";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}
